import { api } from 'balkerne-core/api'
import { useSnackbar } from 'notistack'
import {
  Dispatch,
  SetStateAction,
  ForwardedRef,
  MouseEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import UndoIcon from '@mui/icons-material/Undo'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SearchIcon from '@mui/icons-material/Search'
import {
  Metric,
  MetricDataFilters,
  MetricExportProfile,
  MetricExportProfileMetricConfig,
  useMetricExportProfiles,
} from '../../../hooks/analytics'
import {
  cloneMetricDataFilters,
} from './metricDataFilters'
import {
  buildEmptyMetricSelection,
  buildMetricProfileConfigs,
  buildMetricProfileSnapshot,
  cloneHazardFiltersByHazard,
  HazardFiltersByHazard,
  MetricFacetScalarValue,
  MetricFacetValues,
  MetricSelectionState,
  toFacetValuesFromProfileFilters,
} from './metricExportState'

export type MetricProfileFacetScalarValue = MetricFacetScalarValue
export type MetricProfileFacetValues = MetricFacetValues

export type MetricProfileSelection = MetricSelectionState

export type MetricExportProfileCompatibility = {
  incompatibleMetricIds: Set<number>
  incompatibleCountByHazard: Map<string, number>
}

export type MetricExportProfilePanelHandle = {
  resetIncompatibleMetric: (metricId: number) => void
}

type MetricExportProfilePayload = {
  name: string
  metrics: MetricExportProfileMetricConfig[]
  metric_schema_hashes: Record<string, string>
  hazard_filters: Record<string, MetricDataFilters>
}

type MetricExportProfileActionState = 'idle' | 'creating' | 'saving' | 'deleting'

type Props = {
  metrics: Metric[]
  selections: Record<number, MetricProfileSelection>
  hazardFilters: HazardFiltersByHazard
  onSelectionsChange: Dispatch<SetStateAction<Record<number, MetricProfileSelection>>>
  onHazardFiltersChange: Dispatch<SetStateAction<HazardFiltersByHazard>>
  onCompatibilityChange?: (compatibility: MetricExportProfileCompatibility) => void
  inline?: boolean
}

function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return 'null'
  if (typeof value !== 'object') return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(item => stableStringify(item)).join(',')}]`

  const entries = Object.entries(value as Record<string, unknown>).sort(([left], [right]) => left.localeCompare(right))
  return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${stableStringify(item)}`).join(',')}}`
}

function fnv1aHash(text: string): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i)
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

function buildMetricSchemaHash(metric: Metric): string {
  return fnv1aHash(
    stableStringify({
      value_type: metric.value_type,
      uses_return_periods: Boolean(metric.uses_return_periods),
      facet_schema: metric.facet_schema ?? null,
      facet_defaults: metric.facet_defaults ?? null,
    }),
  )
}

function cloneMetricExportProfile(profile: MetricExportProfile): MetricExportProfile {
  return {
    ...profile,
    metrics: profile.metrics.map(metric => ({
      ...metric,
      facet_filters: metric.facet_filters ? { ...metric.facet_filters } : metric.facet_filters,
      filters: cloneMetricDataFilters(metric.filters),
    })),
    hazard_filters: cloneHazardFiltersByHazard(profile.hazard_filters || {}),
    metric_schema_hashes: { ...(profile.metric_schema_hashes || {}) },
  }
}

function getIncompatibleProfileMetricIds(
  profile: MetricExportProfile | null,
  metricById: Map<number, Metric>,
  schemaHashByMetricId: Map<number, string>,
): Set<number> {
  const incompatible = new Set<number>()
  if (!profile) return incompatible

  profile.metrics.forEach(metricConfig => {
    const metricId = metricConfig.metric_id
    const expectedHash = profile.metric_schema_hashes?.[String(metricId)]
    const currentHash = schemaHashByMetricId.get(metricId)
    if (!metricById.has(metricId) || !expectedHash || !currentHash || expectedHash !== currentHash) {
      incompatible.add(metricId)
    }
  })
  return incompatible
}

function getNextAutoProfileName(profileNames: string[]): string {
  const names = new Set(profileNames.map(name => name.trim().toLowerCase()))
  let index = 1
  while (names.has(`profile #${index}`)) {
    index += 1
  }
  return `Profile #${index}`
}

function MetricExportProfilePanel(
  { metrics, selections, hazardFilters, onSelectionsChange, onHazardFiltersChange, onCompatibilityChange, inline = false }: Props,
  ref: ForwardedRef<MetricExportProfilePanelHandle>,
) {
  const { enqueueSnackbar } = useSnackbar()
  const metricExportProfilesQuery = useMetricExportProfiles()

  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null)
  const [activeProfile, setActiveProfile] = useState<MetricExportProfile | null>(null)
  const [profileName, setProfileName] = useState<string>('')
  const [savedProfileName, setSavedProfileName] = useState<string>('')
  const [savedSelectionSnapshot, setSavedSelectionSnapshot] = useState<string>('')
  const [profileActionState, setProfileActionState] = useState<MetricExportProfileActionState>('idle')
  const [isRenameOpen, setIsRenameOpen] = useState<boolean>(false)
  const [renameDraft, setRenameDraft] = useState<string>('')
  const [selectorAnchorEl, setSelectorAnchorEl] = useState<HTMLElement | null>(null)
  const [selectorSearch, setSelectorSearch] = useState<string>('')

  const profileItems = useMemo(() => metricExportProfilesQuery.data ?? [], [metricExportProfilesQuery.data])
  const metricById = useMemo(() => new Map<number, Metric>(metrics.map(metric => [metric.id, metric])), [metrics])
  const schemaHashByMetricId = useMemo(() => {
    const map = new Map<number, string>()
    metrics.forEach(metric => map.set(metric.id, buildMetricSchemaHash(metric)))
    return map
  }, [metrics])

  const incompatibleProfileMetricIds = useMemo(
    () => getIncompatibleProfileMetricIds(activeProfile, metricById, schemaHashByMetricId),
    [activeProfile, metricById, schemaHashByMetricId],
  )
  const incompatibleMetricCountByHazard = useMemo(() => {
    const counts = new Map<string, number>()
    incompatibleProfileMetricIds.forEach(metricId => {
      const metric = metricById.get(metricId)
      if (!metric) return
      counts.set(metric.hazard, (counts.get(metric.hazard) ?? 0) + 1)
    })
    return counts
  }, [incompatibleProfileMetricIds, metricById])

  const selectedProfile = useMemo(
    () => profileItems.find(profile => profile.id === selectedProfileId) ?? null,
    [profileItems, selectedProfileId],
  )
  const filteredProfileItems = useMemo(() => {
    const q = selectorSearch.trim().toLowerCase()
    if (!q) return profileItems
    return profileItems.filter(profile => profile.name.toLowerCase().includes(q))
  }, [profileItems, selectorSearch])

  const currentSelectionSnapshot = useMemo(
    () =>
      buildMetricProfileSnapshot({
        metrics,
        selections,
        hazardFiltersByHazard: hazardFilters,
      }),
    [hazardFilters, metrics, selections],
  )

  const profileSelected = selectedProfileId !== null && activeProfile?.id === selectedProfileId
  const profileDirty =
    profileSelected && (profileName.trim() !== savedProfileName || currentSelectionSnapshot !== savedSelectionSnapshot)
  const profileActionInProgress = profileActionState !== 'idle'
  const selectorOpen = Boolean(selectorAnchorEl)
  const selectorLabel = profileSelected ? profileName : 'No profile'

  const buildSelectionSnapshot = useCallback(
    (
      nextSelections: Record<number, MetricProfileSelection>,
      nextHazardFilters: HazardFiltersByHazard = hazardFilters,
    ): string =>
      buildMetricProfileSnapshot({
        metrics,
        selections: nextSelections,
        hazardFiltersByHazard: nextHazardFilters,
      }),
    [hazardFilters, metrics],
  )

  const applyProfileToSelections = useCallback(
    (
      profile: MetricExportProfile | null,
    ): { selections: Record<number, MetricProfileSelection>; hazardFilters: HazardFiltersByHazard } => {
      const incompatibleMetricIds = getIncompatibleProfileMetricIds(profile, metricById, schemaHashByMetricId)
      const nextHazardFilters = cloneHazardFiltersByHazard(profile?.hazard_filters || {})

      const next: Record<number, MetricProfileSelection> = {}
      metrics.forEach(metric => {
        next[metric.id] = buildEmptyMetricSelection(metric.id)
      })

      if (!profile) {
        return { selections: next, hazardFilters: {} }
      }

      profile.metrics.forEach(config => {
        const metric = metricById.get(config.metric_id)
        if (!metric || incompatibleMetricIds.has(config.metric_id)) return

        next[metric.id] = {
          id: metric.id,
          selected: true,
          useDefaults: config.use_defaults,
          facetValues: config.use_defaults ? {} : toFacetValuesFromProfileFilters(metric, config.facet_filters),
        }

        if (!(metric.hazard in nextHazardFilters)) {
          const cleanedFilters = cloneMetricDataFilters(config.filters)
          if (cleanedFilters) {
            nextHazardFilters[metric.hazard] = cleanedFilters
          }
        }
      })

      return {
        selections: next,
        hazardFilters: cloneHazardFiltersByHazard(nextHazardFilters),
      }
    },
    [metrics, metricById, schemaHashByMetricId],
  )

  const buildProfilePayloadFromSelections = useCallback(
    (profileNameValue: string): MetricExportProfilePayload => {
      const payloadMetrics = buildMetricProfileConfigs({
        metrics,
        selections,
      })
      const metricSchemaHashes: Record<string, string> = {}

      payloadMetrics.forEach(metricConfig => {
        const metric = metricById.get(metricConfig.metric_id)
        if (!metric) return
        const schemaHash = schemaHashByMetricId.get(metric.id)
        if (schemaHash) {
          metricSchemaHashes[String(metric.id)] = schemaHash
        }
      })

      return {
        name: profileNameValue.trim(),
        metrics: payloadMetrics,
        metric_schema_hashes: metricSchemaHashes,
        hazard_filters: cloneHazardFiltersByHazard(hazardFilters),
      }
    },
    [hazardFilters, metricById, metrics, schemaHashByMetricId, selections],
  )

  const resetIncompatibleMetric = useCallback(
    (metricId: number) => {
      const metric = metricById.get(metricId)

      onSelectionsChange(prev => ({
        ...prev,
        [metricId]: buildEmptyMetricSelection(metricId),
      }))

      setActiveProfile(prev => {
        if (!prev) return prev

        const metric_schema_hashes = { ...(prev.metric_schema_hashes || {}) }
        delete metric_schema_hashes[String(metricId)]
        return {
          ...prev,
          metrics: prev.metrics.filter(entry => entry.metric_id !== metricId),
          metric_schema_hashes,
        }
      })

      if (selectedProfileId !== null) {
        enqueueSnackbar(
          metric
            ? `${metric.label} reset in profile draft. Save profile to persist.`
            : 'Metric reset in profile draft. Save profile to persist.',
          { variant: 'info' },
        )
      }
    },
    [enqueueSnackbar, metricById, onSelectionsChange, selectedProfileId],
  )

  useImperativeHandle(ref, () => ({ resetIncompatibleMetric }), [resetIncompatibleMetric])

  useEffect(() => {
    if (!onCompatibilityChange) return
    onCompatibilityChange({
      incompatibleMetricIds: incompatibleProfileMetricIds,
      incompatibleCountByHazard: incompatibleMetricCountByHazard,
    })
  }, [incompatibleMetricCountByHazard, incompatibleProfileMetricIds, onCompatibilityChange])

  useEffect(() => {
    if (selectedProfileId === null) return
    if (activeProfile?.id === selectedProfileId) return

    const matched = profileItems.find(profile => profile.id === selectedProfileId)
    if (!matched) {
      setSelectedProfileId(null)
      setActiveProfile(null)
      setProfileName('')
      setSavedProfileName('')
      setSavedSelectionSnapshot('')
      return
    }

    const cloned = cloneMetricExportProfile(matched)
    setActiveProfile(cloned)
    setProfileName(cloned.name)
  }, [activeProfile?.id, profileItems, selectedProfileId])

  const handleOpenSelector = (event: MouseEvent<HTMLElement>) => {
    setSelectorAnchorEl(event.currentTarget)
  }

  const handleCloseSelector = () => {
    setSelectorAnchorEl(null)
    setSelectorSearch('')
  }

  const handleProfileSelect = (profile: MetricExportProfile | null) => {
    if (!profile) {
      setSelectedProfileId(null)
      setActiveProfile(null)
      setProfileName('')
      setSavedProfileName('')
      setSavedSelectionSnapshot('')
      handleCloseSelector()
      return
    }

    const cloned = cloneMetricExportProfile(profile)
    setSelectedProfileId(cloned.id)
    setActiveProfile(cloned)
    setProfileName(cloned.name)

    const appliedProfile = applyProfileToSelections(cloned)
    onSelectionsChange(appliedProfile.selections)
    onHazardFiltersChange(appliedProfile.hazardFilters)
    setSavedSelectionSnapshot(buildSelectionSnapshot(appliedProfile.selections, appliedProfile.hazardFilters))
    setSavedProfileName(cloned.name.trim())
    handleCloseSelector()
  }

  const handleCreateProfile = async () => {
    const generatedName = getNextAutoProfileName(profileItems.map(profile => profile.name))
    const payload = buildProfilePayloadFromSelections(generatedName)

    try {
      setProfileActionState('creating')
      const res = await api.post('/analytics/cc/metrics/profiles', payload)
      const created = cloneMetricExportProfile(res.data as MetricExportProfile)

      setSelectedProfileId(created.id)
      setActiveProfile(created)
      setProfileName(created.name)
      setSavedProfileName(created.name.trim())
      setSavedSelectionSnapshot(currentSelectionSnapshot)
      metricExportProfilesQuery.refetch()
      enqueueSnackbar('Metric profile created.', { variant: 'success' })
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.detail || err?.message || 'Failed to create profile.', { variant: 'error' })
    } finally {
      setProfileActionState('idle')
    }
  }

  const handleOpenRename = () => {
    if (!profileSelected) return
    setRenameDraft(profileName)
    setIsRenameOpen(true)
  }

  const handleApplyRename = () => {
    const nextName = renameDraft.trim()
    if (!nextName) {
      enqueueSnackbar('Profile name cannot be empty.', { variant: 'warning' })
      return
    }
    setProfileName(nextName)
    setIsRenameOpen(false)
  }

  const handleSaveProfile = async () => {
    if (selectedProfileId === null) {
      enqueueSnackbar('Select a profile to save.', { variant: 'warning' })
      return
    }
    if (incompatibleProfileMetricIds.size > 0) {
      enqueueSnackbar('Reset incompatible metrics before saving this profile.', { variant: 'warning' })
      return
    }

    const payload = buildProfilePayloadFromSelections(profileName)
    if (!payload.name) {
      enqueueSnackbar('Enter a profile name first.', { variant: 'warning' })
      return
    }

    try {
      setProfileActionState('saving')
      const res = await api.put(`/analytics/cc/metrics/profiles/${selectedProfileId}`, payload)
      const updated = cloneMetricExportProfile(res.data as MetricExportProfile)

      setActiveProfile(updated)
      setProfileName(updated.name)
      setSavedProfileName(updated.name.trim())
      setSavedSelectionSnapshot(currentSelectionSnapshot)
      metricExportProfilesQuery.refetch()
      enqueueSnackbar('Metric profile saved.', { variant: 'success' })
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.detail || err?.message || 'Failed to save profile.', { variant: 'error' })
    } finally {
      setProfileActionState('idle')
    }
  }

  const handleDiscardChanges = () => {
    if (!profileSelected || !selectedProfile) return

    const cloned = cloneMetricExportProfile(selectedProfile)
    setActiveProfile(cloned)
    setProfileName(cloned.name)

    const appliedProfile = applyProfileToSelections(cloned)
    onSelectionsChange(appliedProfile.selections)
    onHazardFiltersChange(appliedProfile.hazardFilters)
    setSavedProfileName(cloned.name.trim())
    setSavedSelectionSnapshot(buildSelectionSnapshot(appliedProfile.selections, appliedProfile.hazardFilters))
  }

  const handleDeleteProfile = async () => {
    if (selectedProfileId === null) return

    try {
      setProfileActionState('deleting')
      await api.delete(`/analytics/cc/metrics/profiles/${selectedProfileId}`)

      setSelectedProfileId(null)
      setActiveProfile(null)
      setProfileName('')
      setSavedProfileName('')
      setSavedSelectionSnapshot('')
      metricExportProfilesQuery.refetch()
      enqueueSnackbar('Metric profile deleted.', { variant: 'success' })
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.detail || err?.message || 'Failed to delete profile.', { variant: 'error' })
    } finally {
      setProfileActionState('idle')
    }
  }

  const incompatibilityMessage = `${incompatibleProfileMetricIds.size} saved metric${
    incompatibleProfileMetricIds.size === 1 ? '' : 's'
  } incompatible with current schemas. Reset incompatible rows or discard/delete profile.`

  return (
    <Box
      sx={
        inline
          ? { p: 0 }
          : {
              px: 2,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'grey.50',
            }
      }>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        <Box sx={{ width: 14, display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
          {metricExportProfilesQuery.isFetching ? <CircularProgress size={12} /> : null}
        </Box>
        {profileDirty && profileSelected ? <Chip size="small" color="warning" label="Unsaved changes" /> : null}
        <Chip
          size="small"
          variant="outlined"
          label={selectorLabel}
          onClick={handleOpenSelector}
          onDelete={handleOpenSelector}
          deleteIcon={<ExpandMoreIcon fontSize="small" />}
          sx={{
            minWidth: inline ? 180 : 220,
            maxWidth: inline ? 260 : 360,
            justifyContent: 'space-between',
            borderColor: profileSelected ? 'primary.main' : 'divider',
            color: profileSelected ? 'text.primary' : 'text.secondary',
            backgroundColor: profileSelected ? 'action.selected' : 'transparent',
            '.MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
          }}
        />
        <Popover
          open={selectorOpen}
          anchorEl={selectorAnchorEl}
          onClose={handleCloseSelector}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { width: inline ? 280 : 340, p: 1 } } }}
        >
          <Stack spacing={0.8}>
            <TextField
              size="small"
              autoFocus
              placeholder="Search profiles"
              value={selectorSearch}
              onChange={event => setSelectorSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <List dense disablePadding sx={{ maxHeight: 280, overflowY: 'auto' }}>
              <ListItemButton selected={selectedProfileId === null} onClick={() => handleProfileSelect(null)}>
                <ListItemText
                  primary="No profile"
                  secondary={profileItems.length > 0 ? 'Do not use a saved profile' : 'No saved profiles yet'}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItemButton>
              {filteredProfileItems.map(profile => (
                <ListItemButton
                  key={profile.id}
                  selected={selectedProfileId === profile.id}
                  onClick={() => handleProfileSelect(profile)}>
                  <ListItemText
                    primary={profile.name}
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
            {filteredProfileItems.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
                No profiles match search.
              </Typography>
            ) : null}
          </Stack>
        </Popover>
        <Stack direction="row" spacing={0.25} alignItems="center">
          <Tooltip title="Create profile from current selection">
            <span>
              <IconButton size="small" onClick={handleCreateProfile} disabled={profileActionInProgress}>
                <AddCircleOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Save profile changes">
            <span>
              <IconButton
                size="small"
                onClick={handleSaveProfile}
                disabled={
                  profileActionInProgress ||
                  !profileSelected ||
                  !profileDirty ||
                  !profileName.trim() ||
                  incompatibleProfileMetricIds.size > 0
                }>
                <SaveOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {profileSelected && profileDirty ? (
            <Tooltip title="Discard unsaved changes">
              <span>
                <IconButton size="small" onClick={handleDiscardChanges} disabled={profileActionInProgress}>
                  <UndoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          <Tooltip title="Edit profile name">
            <span>
              <IconButton size="small" onClick={handleOpenRename} disabled={profileActionInProgress || !profileSelected}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete profile">
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={handleDeleteProfile}
                disabled={profileActionInProgress || !profileSelected}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        {profileSelected && incompatibleProfileMetricIds.size > 0 ? (
          <Tooltip title={incompatibilityMessage}>
            <Chip size="small" color="error" variant="outlined" label={`${incompatibleProfileMetricIds.size} incompatible`} />
          </Tooltip>
        ) : null}
      </Stack>

      <Dialog open={isRenameOpen} onClose={() => setIsRenameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit profile name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Profile name"
            value={renameDraft}
            onChange={event => setRenameDraft(event.target.value)}
            sx={{ mt: 0.5 }}
          />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setIsRenameOpen(false)}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleApplyRename} disabled={!renameDraft.trim()}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default forwardRef(MetricExportProfilePanel)
